import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ChildField, FormData, FormField, FormSchema } from '@foundation/shared';
import { evalCondition } from '@foundation/shared';
import type { UploadedPhoto } from '../lib/upload';

/**
 * ตัวแสดงแบบฟอร์มยืดหยุ่น
 *
 * ใช้ evalCondition ตัวเดียวกับที่ Worker ใช้ตอน validate — ถ้าแยกเขียนจะเกิดกรณี
 * "หน้าจอให้ผ่านแต่เซิร์ฟเวอร์ปฏิเสธ" ซึ่ง debug ยากมากเมื่ออยู่หน้างาน
 */

export interface FormRendererProps {
  schema: FormSchema;
  value: FormData;
  onChange: (next: FormData) => void;
  /** ซ่อน section ที่เป็นข้อมูลอ่อนไหวเมื่อยังไม่มีความยินยอม */
  hideSensitive?: boolean;
  /** ฟิลด์ชนิด photo — ให้หน้าเรียกจัดการการถ่าย/อัปโหลดเอง */
  photos: Record<string, UploadedPhoto[]>;
  onCapture: (fieldKey: string, max: number) => void;
  onRemovePhoto: (fieldKey: string, key: string) => void;
  /** ตัวเลือกที่ดึงจากตารางอื่น (optionsFrom) */
  dynamicOptions?: Record<string, Array<{ value: string; label: string }>>;
  /** ข้อความผิดพลาดรายฟิลด์จากเซิร์ฟเวอร์ */
  errors?: Record<string, string>;
  disabled?: boolean;
}

export function FormRenderer(props: FormRendererProps) {
  const { schema, value, onChange, hideSensitive, errors, disabled } = props;

  const set = useCallback(
    (key: string, v: unknown) => onChange({ ...value, [key]: v as never }),
    [value, onChange],
  );

  const sections = useMemo(
    () =>
      schema.sections.filter(
        (s) => !(hideSensitive && s.sensitive) && evalCondition(s.showIf, value),
      ),
    [schema, value, hideSensitive],
  );

  return (
    <>
      {hideSensitive && schema.sections.some((s) => s.sensitive) && (
        <div className="alert info small">
          ผู้รับความช่วยเหลือยังไม่ได้ให้ความยินยอมเก็บข้อมูลอ่อนไหว
          — ส่วนสุขภาพถูกซ่อนไว้ตามข้อกำหนด PDPA
        </div>
      )}

      {sections.map((section) => (
        <div className="card" key={section.key}>
          <h2>{section.title}</h2>
          {section.description && (
            <p className="muted small" style={{ marginTop: -6 }}>
              {section.description}
            </p>
          )}

          {section.fields
            .filter((f) => evalCondition(f.showIf, value))
            .map((field) => (
              <FieldControl
                key={field.key}
                field={field}
                value={value[field.key]}
                onChange={(v) => set(field.key, v)}
                error={errors?.[field.key]}
                disabled={disabled}
                photos={props.photos[field.key] ?? []}
                onCapture={props.onCapture}
                onRemovePhoto={props.onRemovePhoto}
                dynamicOptions={props.dynamicOptions}
              />
            ))}
        </div>
      ))}
    </>
  );
}

interface FieldControlProps {
  field: FormField | ChildField;
  value: unknown;
  onChange: (v: unknown) => void;
  error?: string;
  disabled?: boolean;
  photos: UploadedPhoto[];
  onCapture: (fieldKey: string, max: number) => void;
  onRemovePhoto: (fieldKey: string, key: string) => void;
  dynamicOptions?: Record<string, Array<{ value: string; label: string }>>;
  /** true = อยู่ในแถวของ repeater (ซ่อน label ซ้ำ) */
  compact?: boolean;
}

function FieldControl(p: FieldControlProps) {
  const f = p.field as FormField;
  const id = `field-${f.key}`;

  if (f.type === 'note') {
    return (
      <div className="alert info small" style={{ marginBottom: 12 }}>
        {f.label}
      </div>
    );
  }

  const label = (
    <label htmlFor={id}>
      {f.label}
      {f.required && <span style={{ color: 'var(--c-danger)' }}> *</span>}
    </label>
  );

  const help = f.help ? (
    <div className="muted small" style={{ marginTop: 4 }}>
      {f.help}
    </div>
  ) : null;

  const error = p.error ? (
    <div className="small" style={{ color: 'var(--c-danger)', marginTop: 4 }}>
      {p.error}
    </div>
  ) : null;

  const options =
    f.options ?? (f.optionsFrom ? (p.dynamicOptions?.[f.optionsFrom] ?? []) : []);

  switch (f.type) {
    case 'text':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="text"
            value={String(p.value ?? '')}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            disabled={p.disabled}
            onChange={(e) => p.onChange(e.target.value)}
          />
          {help}
          {error}
        </div>
      );

    case 'textarea':
      return (
        <div className="field">
          {label}
          <textarea
            id={id}
            value={String(p.value ?? '')}
            maxLength={f.maxLength}
            placeholder={f.placeholder}
            disabled={p.disabled}
            onChange={(e) => p.onChange(e.target.value)}
          />
          {help}
          {error}
        </div>
      );

    case 'number':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="text"
            inputMode="numeric"
            value={p.value === null || p.value === undefined ? '' : String(p.value)}
            disabled={p.disabled}
            onChange={(e) => {
              const raw = e.target.value.replace(/[^\d.-]/g, '');
              p.onChange(raw === '' ? null : Number(raw));
            }}
          />
          {help}
          {error}
        </div>
      );

    case 'currency':
      return <CurrencyField id={id} label={label} help={help} error={error} {...p} />;

    case 'date':
      return (
        <div className="field">
          {label}
          <input
            id={id}
            type="date"
            value={String(p.value ?? '')}
            disabled={p.disabled}
            onChange={(e) => p.onChange(e.target.value || null)}
          />
          {help}
          {error}
        </div>
      );

    case 'boolean':
      return (
        <div className="field">
          {label}
          <div className="btn-row" style={{ marginTop: 4 }}>
            <button
              type="button"
              className={p.value === true ? '' : 'secondary'}
              disabled={p.disabled}
              onClick={() => p.onChange(true)}
            >
              ใช่
            </button>
            <button
              type="button"
              className={p.value === false ? '' : 'secondary'}
              disabled={p.disabled}
              onClick={() => p.onChange(false)}
            >
              ไม่ใช่
            </button>
          </div>
          {help}
          {error}
        </div>
      );

    case 'select':
      // ตัวเลือกน้อย → ปุ่มใหญ่ กดง่ายกลางแดด · ตัวเลือกเยอะ → dropdown
      return options.length <= 5 ? (
        <div className="field">
          {label}
          <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                className={p.value === o.value ? '' : 'secondary'}
                disabled={p.disabled}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => p.onChange(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
          {help}
          {error}
        </div>
      ) : (
        <div className="field">
          {label}
          <select
            id={id}
            value={String(p.value ?? '')}
            disabled={p.disabled}
            onChange={(e) => p.onChange(e.target.value || null)}
          >
            <option value="">— เลือก —</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {help}
          {error}
        </div>
      );

    case 'multiselect': {
      const selected = Array.isArray(p.value) ? (p.value as string[]) : [];
      return (
        <div className="field">
          {label}
          <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
            {options.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  className={on ? '' : 'secondary'}
                  disabled={p.disabled}
                  style={{ justifyContent: 'flex-start' }}
                  onClick={() =>
                    p.onChange(
                      on ? selected.filter((v) => v !== o.value) : [...selected, o.value],
                    )
                  }
                >
                  {on ? '✓ ' : ''}
                  {o.label}
                </button>
              );
            })}
          </div>
          {help}
          {error}
        </div>
      );
    }

    case 'rating': {
      const current = Number(p.value ?? 0);
      return (
        <div className="field">
          {label}
          <div className="btn-row" style={{ marginTop: 4 }}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                className={current === n ? '' : 'secondary'}
                disabled={p.disabled}
                onClick={() => p.onChange(n)}
              >
                {n}
              </button>
            ))}
          </div>
          {help}
          {error}
        </div>
      );
    }

    case 'photo': {
      const max = f.max ?? 5;
      return (
        <div className="field">
          {label}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0' }}>
            {p.photos.map((photo) => (
              <div key={photo.key} style={{ position: 'relative' }}>
                <img
                  src={photo.previewUrl}
                  alt=""
                  style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 10 }}
                />
                {!p.disabled && (
                  <button
                    type="button"
                    aria-label="ลบรูป"
                    onClick={() => p.onRemovePhoto(f.key, photo.key)}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      minHeight: 26,
                      width: 26,
                      padding: 0,
                      borderRadius: '50%',
                      background: 'var(--c-danger)',
                    }}
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
          {p.photos.length < max && !p.disabled && (
            <button type="button" className="secondary block" onClick={() => p.onCapture(f.key, max)}>
              📷 ถ่ายรูป ({p.photos.length}/{max})
            </button>
          )}
          {help}
          {error}
        </div>
      );
    }

    case 'gps': {
      const coords = p.value as { lat: number; lng: number } | null;
      return (
        <div className="field">
          {label}
          <div className="small muted" style={{ marginBottom: 6 }}>
            {coords ? `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}` : 'ยังไม่ได้ปักหมุด'}
          </div>
          <button
            type="button"
            className="secondary block"
            disabled={p.disabled}
            onClick={async () => {
              const { getCurrentPosition } = await import('../lib/image');
              const pos = await getCurrentPosition();
              if (pos) p.onChange({ lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy });
            }}
          >
            📍 ปักหมุดตำแหน่งปัจจุบัน
          </button>
          {help}
          {error}
        </div>
      );
    }

    case 'signature':
      return (
        <div className="field">
          {label}
          <div className="alert info small">ลายเซ็นอิเล็กทรอนิกส์จะเปิดใช้พร้อมการส่งมอบความช่วยเหลือในเฟส 4</div>
        </div>
      );

    case 'repeater': {
      const rows = Array.isArray(p.value) ? (p.value as Array<Record<string, unknown>>) : [];
      const max = f.max ?? 20;
      return (
        <div className="field">
          {label}
          {rows.map((row, i) => (
            <div
              key={i}
              className="card"
              style={{ marginTop: 8, marginBottom: 8, background: 'var(--c-bg)' }}
            >
              <div className="row" style={{ marginBottom: 6 }}>
                <strong className="small">รายการที่ {i + 1}</strong>
                {!p.disabled && (
                  <button
                    type="button"
                    className="secondary"
                    style={{ minHeight: 34, padding: '4px 12px' }}
                    onClick={() => p.onChange(rows.filter((_, j) => j !== i))}
                  >
                    ลบ
                  </button>
                )}
              </div>
              {(f.fields ?? [])
                .filter((child) => evalCondition(child.showIf, row as FormData))
                .map((child) => (
                  <FieldControl
                    key={child.key}
                    field={child}
                    value={row[child.key]}
                    disabled={p.disabled}
                    photos={[]}
                    onCapture={() => undefined}
                    onRemovePhoto={() => undefined}
                    dynamicOptions={p.dynamicOptions}
                    onChange={(v) =>
                      p.onChange(rows.map((r, j) => (j === i ? { ...r, [child.key]: v } : r)))
                    }
                  />
                ))}
            </div>
          ))}
          {rows.length < max && !p.disabled && (
            <button
              type="button"
              className="secondary block"
              onClick={() => p.onChange([...rows, {}])}
            >
              + เพิ่มรายการ
            </button>
          )}
          {help}
          {error}
        </div>
      );
    }

    default:
      return null;
  }
}

/**
 * ช่องจำนวนเงิน — ผู้ใช้กรอกเป็นบาท ระบบเก็บเป็นสตางค์
 * เก็บสตริงระหว่างพิมพ์ ไม่งั้นการพิมพ์ "10." จะถูกปัดทิ้งทันที
 */
interface CurrencyFieldProps extends Omit<FieldControlProps, 'error'> {
  id: string;
  label: ReactNode;
  help: ReactNode;
  error: ReactNode;
}

function CurrencyField(p: CurrencyFieldProps) {
  const satang = typeof p.value === 'number' ? p.value : null;
  const [text, setText] = useState(satang === null ? '' : String(satang / 100));

  return (
    <div className="field">
      {p.label}
      <div style={{ position: 'relative' }}>
        <input
          id={p.id}
          type="text"
          inputMode="decimal"
          value={text}
          disabled={p.disabled}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^\d.]/g, '');
            setText(raw);
            const baht = Number(raw);
            p.onChange(raw === '' || Number.isNaN(baht) ? null : Math.round(baht * 100));
          }}
          style={{ paddingInlineEnd: 46 }}
        />
        <span
          className="muted small"
          style={{ position: 'absolute', insetInlineEnd: 14, top: 14 }}
        >
          บาท
        </span>
      </div>
      {p.help}
      {p.error}
    </div>
  );
}
